// SIWS CUSTOM (2026-05-17): Floor Mat color picker — floor-mat-picker.js
// 2 colors (Blue / White imprint). Step 1, min 0 per color. Per-color
// discount kicks in at qty ≥ 2 via Shopify variant-scoped Discount rules.
//
// Pricing model (a per iter spec Q2): variant.price = the full single-unit
// price. Shopify Discount rule reduces the line at qty ≥ 2 at checkout.
// JS hardcodes the discounted "bulk" price for chaser/summary display so
// dealers see the unlock target before reaching the cart.
//
// ATC payload: ONE cart line item per non-zero color (mats use distinct
// Shopify variants for Blue / White, so each variant needs its own line so
// Shopify's per-variant discount rule fires correctly).
//
// Initialized on DOMContentLoaded for all .floor-mat-picker elements.

'use strict';

const SIWS_MAT_COLORS = [
  { id: 'blue',  name: 'Blue',  swatch: '#1A56DB', titleSubstr: 'Blue'  },
  { id: 'white', name: 'White', swatch: '#FFFFFF', titleSubstr: 'White' },
];

// Per-mat pricing tiers. Keyed by product title substring (matches the
// existing SIWS_MAT_DISCOUNTS pattern at cart.js:332). single = full per-unit
// price (variant.price); bulk = per-unit price after Shopify Discount rule
// applies at qty ≥ 2.
const SIWS_MAT_TIERS = {
  'Anti-Fatigue': { single: 262, bulk: 170 },
  'Berber':       { single: 615, bulk: 495 },
  'Super Scrape': { single: 435, bulk: 350 },
};

const SIWS_MAT_BULK_THRESHOLD = 2;
const SIWS_MAT_STEP = 1;

function siwsMatTierForTitle(title) {
  if (!title) return null;
  for (const [key, tier] of Object.entries(SIWS_MAT_TIERS)) {
    if (title.includes(key)) return tier;
  }
  return null;
}

function siwsMatUnitPrice(tier, qty) {
  if (!tier) return 0;
  return qty >= SIWS_MAT_BULK_THRESHOLD ? tier.bulk : tier.single;
}

class SIWSFloorMatPicker {
  constructor(el) {
    this.el         = el;
    this.colorQtys  = { blue: 0, white: 0 };
    this.productTitle = el.dataset.productTitle || '';
    this.tier       = siwsMatTierForTitle(this.productTitle);

    // Parse variant list emitted by section Liquid. Map color id → variant id
    // by substring match on variant.title. Substring fallback per iter spec Q1
    // ("Blue" / "White" or variants thereof — match defensively).
    let variants = [];
    try { variants = JSON.parse(el.dataset.variants || '[]'); } catch (e) {}

    this.variantIdByColor = {};
    SIWS_MAT_COLORS.forEach(color => {
      const match = variants.find(v =>
        v.title && v.title.toLowerCase().includes(color.titleSubstr.toLowerCase())
      );
      this.variantIdByColor[color.id] = match ? match.id : null;
    });

    this.init();
  }

  init() {
    this._renderColorCards();
    this._bindEvents();
    this._update();
  }

  _totalQty() {
    return Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
  }

  _selectedColors() {
    return SIWS_MAT_COLORS.filter(c => (this.colorQtys[c.id] || 0) > 0);
  }

  _increment(id) {
    this.colorQtys[id] = (this.colorQtys[id] || 0) + SIWS_MAT_STEP;
    this._update();
  }

  _decrement(id) {
    const current = this.colorQtys[id] || 0;
    if (current <= 0) return;
    this.colorQtys[id] = current - SIWS_MAT_STEP;
    this._update();
  }

  _renderColorCards() {
    const container = this.el.querySelector('#floor-mat-color-cards');
    if (!container) return;

    const grid = document.createElement('div');
    grid.className = 'screwdriver-color-grid';

    SIWS_MAT_COLORS.forEach(color => {
      grid.appendChild(this._buildCardEl(color));
    });

    container.appendChild(grid);
  }

  _buildCardEl(color) {
    const card = document.createElement('div');
    card.className       = 'screwdriver-color-card';
    card.dataset.colorId = color.id;

    const swatchWrap = document.createElement('div');
    swatchWrap.className = 'screwdriver-color-card__swatch-wrap';

    const swatch = document.createElement('span');
    swatch.className        = 'screwdriver-color-card__swatch';
    swatch.style.background = color.swatch;
    // White swatch needs a visible border so it doesn't disappear on a white card.
    if (color.id === 'white') {
      swatch.style.border = '1px solid rgba(var(--color-foreground), 0.25)';
    }
    swatch.setAttribute('aria-hidden', 'true');

    swatchWrap.appendChild(swatch);

    const name = document.createElement('span');
    name.className   = 'screwdriver-color-card__name';
    name.textContent = color.name;

    const stepper = document.createElement('div');
    stepper.className = 'screwdriver-color-card__stepper';

    const minusBtn = document.createElement('button');
    minusBtn.type            = 'button';
    minusBtn.className       = 'screwdriver-color-card__minus';
    minusBtn.dataset.colorId = color.id;
    minusBtn.textContent     = '−';
    minusBtn.disabled        = true;
    minusBtn.setAttribute('aria-label', `Remove one ${color.name}`);

    const qtySpan = document.createElement('span');
    qtySpan.className   = 'screwdriver-color-card__qty';
    qtySpan.textContent = '0';

    const plusBtn = document.createElement('button');
    plusBtn.type            = 'button';
    plusBtn.className       = 'screwdriver-color-card__plus';
    plusBtn.dataset.colorId = color.id;
    plusBtn.textContent     = '+';
    plusBtn.setAttribute('aria-label', `Add one ${color.name}`);

    stepper.appendChild(minusBtn);
    stepper.appendChild(qtySpan);
    stepper.appendChild(plusBtn);

    card.appendChild(swatchWrap);
    card.appendChild(name);
    card.appendChild(stepper);

    return card;
  }

  _bindEvents() {
    this.el.addEventListener('click', e => {
      const plusBtn  = e.target.closest('.screwdriver-color-card__plus');
      const minusBtn = e.target.closest('.screwdriver-color-card__minus');
      if (plusBtn)  this._increment(plusBtn.dataset.colorId);
      if (minusBtn) this._decrement(minusBtn.dataset.colorId);
    });

    const atcBtn = this.el.querySelector('#floor-mat-atc-btn');
    if (atcBtn) atcBtn.addEventListener('click', () => this._addToCart());
  }

  _update() {
    this._updateCardStates();
    this._updateDiscountMsg();
    this._updateSummary();
    this._updateButton();
  }

  _updateCardStates() {
    this.el.querySelectorAll('.screwdriver-color-card').forEach(card => {
      const id  = card.dataset.colorId;
      const qty = this.colorQtys[id] || 0;

      card.classList.toggle('screwdriver-color-card--selected', qty > 0);

      const minusBtn = card.querySelector('.screwdriver-color-card__minus');
      const qtySpan  = card.querySelector('.screwdriver-color-card__qty');

      if (qtySpan)  qtySpan.textContent = qty;
      if (minusBtn) minusBtn.disabled   = qty === 0;
    });
  }

  _updateDiscountMsg() {
    const msgEl = this.el.querySelector('#floor-mat-discount-msg');
    if (!msgEl) return;

    if (!this.tier) {
      msgEl.hidden = true;
      msgEl.innerHTML = '';
      return;
    }

    const total = this._totalQty();

    // Hide entirely when no color is selected — keeps the PDP clean before
    // first interaction. Once any color has qty ≥ 1, render BOTH per-color
    // rows so the dealer sees parallel status for the other color too.
    if (total <= 0) {
      msgEl.hidden = true;
      msgEl.innerHTML = '';
      return;
    }

    const rows = SIWS_MAT_COLORS.map(color => {
      const qty = this.colorQtys[color.id] || 0;
      if (qty >= SIWS_MAT_BULK_THRESHOLD) {
        return `<div class="floor-mat-tier-row floor-mat-tier-row--unlocked">✅ $${this.tier.bulk}/each ${color.name} pricing unlocked — best value!</div>`;
      }
      const needed = SIWS_MAT_BULK_THRESHOLD - qty;
      return `<div class="floor-mat-tier-row floor-mat-tier-row--chase">Add ${needed} more ${color.name} to unlock $${this.tier.bulk}/each pricing.</div>`;
    }).join('');

    msgEl.innerHTML = rows;
    msgEl.hidden    = false;
  }

  _updateSummary() {
    const summaryEl = this.el.querySelector('#floor-mat-summary');
    if (!summaryEl) return;

    const selected = this._selectedColors();
    if (selected.length === 0 || !this.tier) {
      summaryEl.hidden = true;
      summaryEl.innerHTML = '';
      return;
    }

    const hStyle  = 'text-align:right;padding:0 0 0.6rem;font-size:1.1rem;color:rgba(var(--color-foreground),0.45);font-weight:600;';
    const hStyleL = 'text-align:left;padding:0 0 0.6rem;font-size:1.1rem;color:rgba(var(--color-foreground),0.45);font-weight:600;';

    let grandTotal = 0;

    const rows = selected.map(c => {
      const qty       = this.colorQtys[c.id];
      const unitPrice = siwsMatUnitPrice(this.tier, qty);
      const lineCost  = qty * unitPrice;
      grandTotal     += lineCost;

      const dot = `<span class="screwdriver-summary__dot" style="background:${c.swatch};${c.id === 'white' ? 'border:1px solid rgba(var(--color-foreground),0.25);' : ''}"></span>`;

      return `<tr>
        <td>${dot}${c.name}</td>
        <td class="screwdriver-summary__qty-cell">${qty}</td>
        <td class="screwdriver-summary__qty-cell">$${unitPrice.toFixed(2)}</td>
        <td class="screwdriver-summary__qty-cell">$${lineCost.toFixed(2)}</td>
      </tr>`;
    }).join('');

    summaryEl.innerHTML = `
      <h4 class="screwdriver-summary__title">Adding to Cart</h4>
      <table class="screwdriver-summary__table">
        <thead><tr>
          <th style="${hStyleL}">Color</th>
          <th style="${hStyle}">Qty</th>
          <th style="${hStyle}">Unit Price</th>
          <th style="${hStyle}">Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="screwdriver-summary__total-row">
            <td colspan="3">Grand Total</td>
            <td class="screwdriver-summary__qty-cell">$${grandTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>`;
    summaryEl.hidden = false;
  }

  _updateButton() {
    const atcBtn = this.el.querySelector('#floor-mat-atc-btn');
    if (!atcBtn) return;

    const total = this._totalQty();

    if (total === 0 || !this.tier) {
      atcBtn.disabled    = true;
      atcBtn.textContent = 'Select colors to continue';
      return;
    }

    let grandTotal = 0;
    SIWS_MAT_COLORS.forEach(c => {
      const qty = this.colorQtys[c.id] || 0;
      if (qty > 0) grandTotal += qty * siwsMatUnitPrice(this.tier, qty);
    });

    atcBtn.disabled    = false;
    atcBtn.textContent = `Add to Cart — $${grandTotal.toFixed(2)}`;
  }

  _showError(msg) {
    const el = this.el.querySelector('#floor-mat-atc-error');
    if (!el) return;
    el.textContent = msg;
    el.hidden      = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _hideError() {
    const el = this.el.querySelector('#floor-mat-atc-error');
    if (el) el.hidden = true;
  }

  async _addToCart() {
    const atcBtn = this.el.querySelector('#floor-mat-atc-btn');
    if (!atcBtn || atcBtn.disabled) return;

    const selected = this._selectedColors();
    if (selected.length === 0) return;

    // Build one cart line item per non-zero color. Mats use distinct Shopify
    // variants per color so Shopify's per-variant Discount rule fires
    // independently for each line.
    const items = [];
    let missingVariant = false;
    selected.forEach(c => {
      const variantId = this.variantIdByColor[c.id];
      if (!variantId) {
        missingVariant = true;
        return;
      }
      items.push({
        id: parseInt(variantId, 10),
        quantity: this.colorQtys[c.id],
        properties: { 'Imprint Color': c.name },
      });
    });

    if (missingVariant || items.length === 0) {
      this._showError('Variant configuration error. Please refresh and try again.');
      return;
    }

    const originalText = atcBtn.textContent;
    atcBtn.disabled    = true;
    atcBtn.textContent = 'Adding to cart…';
    this._hideError();

    try {
      // SIWS CUSTOM: augment body with Section Rendering API hints so the Dawn
      // cart drawer/notification can re-render itself from the response.
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = {
        items,
        sections:     cart ? cart.getSectionsToRender().map((s) => s.id) : ['cart-icon-bubble'],
        sections_url: window.location.pathname,
      };

      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }

      const responseData = await res.json();
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'siws-floor-mat', cartData: responseData });
      }
      if (cart) {
        cart.renderContents(responseData);
      } else {
        // Page-mode — stay on PDP, refresh cart bubble.
        const newBubbleHTML = responseData.sections && responseData.sections['cart-icon-bubble'];
        const liveBubble = document.getElementById('cart-icon-bubble');
        if (newBubbleHTML && liveBubble) {
          const parsed = new DOMParser().parseFromString(newBubbleHTML, 'text/html');
          const inner  = parsed.querySelector('.shopify-section');
          if (inner) liveBubble.innerHTML = inner.innerHTML;
        }
        // Clear picker state so a re-click can't double-add.
        this.colorQtys = { blue: 0, white: 0 };
        this._update();
      }

    } catch (err) {
      console.error('[SIWS] Floor mat add-to-cart error:', err);
      atcBtn.disabled    = false;
      atcBtn.textContent = originalText;
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }
}

function _siwsInitFloorMatPickers() {
  document.querySelectorAll('.floor-mat-picker').forEach(el => {
    new SIWSFloorMatPicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _siwsInitFloorMatPickers);
} else {
  _siwsInitFloorMatPickers();
}
